/* eslint-disable react-hooks/exhaustive-deps */
import { useAppSelector } from 'hooks'
import { country } from 'store/AIMetricOT/reducerSlice'
import {
  calculateCountryDataFromMetricValue,
  calculateMetricGroupForTable,
  calculateWeightedGlobalBarChart,
  generateGlobalBarChartData,
  getCountryCodeFromCountryLongCode,
  getCountryNameFromCountryLongCode,
  reCalculateMetricGroupsForAYear,
} from 'utils'
import ReportHeading from './components/ReportHeading'
import * as Flags from 'country-flag-icons/react/3x2'
import ReportTable from './components/ReportTable'
import './style.scss'
import { useMemo } from 'react'
import { METRIC_ENDPOINT_WEIGHT, METRIC_ENDPOINT_RANK, NUMBER_OF_YEAR_IN_REPORT } from 'constant'
import { BarChartGlobalData, Country } from 'types'
import styled from 'styled-components'
import { Grid } from '@mui/material'
import Overview from './components/Overview'
import ReportBarChart from './components/ReportBarChart'
import SlopeChartReport from 'components/common/SlopeChartReport/SlopeChartReport'
import Note from './components/Note'

const ReportComponents: React.FC = () => {
  const countryData = useAppSelector(({ vibrancy }) => vibrancy.countryData)
  const countryLongCode = useAppSelector(country)
  const countryShortCode = getCountryCodeFromCountryLongCode(countryData, countryLongCode) ?? 'US'
  const { yearFilter, metricValue } = useAppSelector(({ vibrancy }) => vibrancy.configuration)
  const metricGroupFromRedux = useAppSelector(({ vibrancy }) => vibrancy.metricGroup)
  const renderChartTimes = useAppSelector(({ vibrancy }) => vibrancy.renderChartTimes)

  const yearRangeOverview = [yearFilter.currentYear, yearFilter.currentYear - 1] //yearFilter.yearRange.toSorted((a, b) => b - a).slice(0, NUMBER_OF_YEAR_IN_REPORT)
  const metricGroups = calculateMetricGroupForTable(metricGroupFromRedux, {
    metricValue,
  })
  const Flag = (Flags as any)[countryShortCode]

  const renderChartData = calculateWeightedGlobalBarChart(
    metricGroups,
    metricValue,
    countryData,
    yearFilter?.currentYear,
  )

  const barChartGlobalData = useMemo<BarChartGlobalData>(() => {
    if (metricGroups.length === 0 || countryData.length === 0 || yearFilter.yearRange.length === 0) return []

    const dataReturn = generateGlobalBarChartData(metricGroups, renderChartData, {
      globalYear: 2024,
      metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
      metricEndpointRank: METRIC_ENDPOINT_RANK,
      metricValue: metricValue,
    })
    return dataReturn
  }, [countryData.length, metricGroups.length, countryLongCode, renderChartTimes])

  // logic for ReportHeading:
  const countryRank =
    barChartGlobalData.findIndex(
      (countryEl) => countryEl.countryName === getCountryNameFromCountryLongCode(countryData, countryLongCode),
    ) + 1
  const countryName = countryData?.find((element) => element?.countryLongCode === countryLongCode)?.countryName

  const tableData = useMemo<Country[]>(() => {
    return calculateCountryDataFromMetricValue(metricGroups, renderChartData, {
      globalYear: yearFilter.currentYear,
      metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
      metricEndpointRank: METRIC_ENDPOINT_RANK,
      metricValue: metricValue,
    })
  }, [countryData.length, metricGroups.length, renderChartTimes, countryLongCode])

  // console.log('___111tableData', tableData)
  // logic for Report Overview:
  const downloadCountryDataFunc = (year: number) => {
    let newRenderChartData = renderChartData
    let modifiedMetricGroups = metricGroups
    if (year !== yearFilter.currentYear && year >= Math.min(...yearFilter.yearRange)) {
      modifiedMetricGroups = reCalculateMetricGroupsForAYear(metricGroupFromRedux, countryData, metricValue, year)
      newRenderChartData = calculateWeightedGlobalBarChart(modifiedMetricGroups, metricValue, countryData, year)
    }
    const countryDataYear = calculateCountryDataFromMetricValue(metricGroups, newRenderChartData, {
      globalYear: year,
      metricEndpointWeight: METRIC_ENDPOINT_WEIGHT,
      metricEndpointRank: METRIC_ENDPOINT_RANK,
      metricValue: metricValue,
      rankingLogic: {
        metricRanking: true,
        pillarRanking: true,
        yearRankingEachCountry: true,
      },
    })

    const selectedCountryData = countryDataYear?.find((element) => element?.countryLongCode === countryLongCode)
    // console.log(`___overview-countryDataYear: ${year}`, selectedCountryData)
    return {
      year: year,
      countryReportData: selectedCountryData,
      totalCountry: countryDataYear?.length,
    }
  }

  const downloadCountryDataYears = yearRangeOverview.map((year) => downloadCountryDataFunc(year))
  // console.log('___overview-downloadCountryDataYears: ', downloadCountryDataYears)

  return (
    <div
      style={{
        width: '1200px',
        height: '1698px !important',
        padding: '25px',
        margin: '20px',
        marginTop: '30px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <ReportHeading
        metricValue={metricValue === 'absolute' ? 'Absolute Metrics' : 'Per Capita Metrics'}
        rank={countryRank}
        currentYear={yearFilter?.currentYear}
        countryName={countryName}
        Flag={Flag}
      />
      <Grid
        container
        spacing={2}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          minHeight: '200px',
          my: '20px',
        }}
      >
        <Grid item xs={4}>
          <GridWrapper>
            <SlopeChartReport name={countryName || ''}></SlopeChartReport>
          </GridWrapper>
        </Grid>
        <Grid item xs={4}>
          <GridWrapper>
            <ReportBarChart currentYear={yearFilter?.currentYear} data={barChartGlobalData} />
          </GridWrapper>
        </Grid>
        <Grid item xs={4}>
          <GridWrapper>
            <Overview
              data={downloadCountryDataYears}
              metricGroups={metricGroups}
              countries={countryData}
              yearRange={yearRangeOverview}
            />
          </GridWrapper>
        </Grid>
      </Grid>
      <ReportTable
        countryData={tableData}
        countryName={getCountryNameFromCountryLongCode(countryData, countryLongCode) || ''}
        currentYear={yearFilter.currentYear}
        metricGroups={metricGroups}
        metricValue={metricValue}
        countryShortCode={countryShortCode}
      />
      <Note />
    </div>
  )
}

export default ReportComponents
const GridWrapper = styled.div`
  display: flex;
  padding: 0px;
  flex-direction: column;
  align-items: center;
  background: #fff;
`
